/**
 * 租车相关接口
 * 对应 Apipost: 用户端 > 前台租车 / 租车订单
 */

import { http } from "@/http/axios";

// ==================== 类型定义 ====================

/** 租车产品项 */
export interface CarProductItem {
  id: number;
  title: string;
  brand: string;
  model: string;
  seats: number;
  seats_text: string;
  car_type: string;
  gearbox: number;
  gearbox_text: string;
  service_type: number;
  service_type_text: string;
  has_insurance: boolean;
  insurance_text: string;
  is_no_deposit: boolean;
  deposit_type_text: string;
  has_luxury_interior: boolean;
  daily_price: number;
  daily_price_text: string;
  rating: number;
  reviews_count: number;
  cover_image: string;
  cover_url: string;
}

/** 租车产品列表响应 */
export interface CarListResponse {
  code: number;
  message: string;
  data: {
    total: number;
    page: number;
    limit: number;
    list: CarProductItem[];
  };
}

/** 品牌项 */
export interface BrandItem {
  brand: string;
  count: number;
}

/** 品牌列表响应 */
export interface BrandListResponse {
  code: number;
  message: string;
  data: BrandItem[];
}

/** 车型分类项 */
export interface CarTypeItem {
  car_type: string;
  count: number;
}

/** 车型分类列表响应 */
export interface CarTypeListResponse {
  code: number;
  message: string;
  data: CarTypeItem[];
}

/** 车辆详情项 */
export interface CarDetailItem {
  id: number;
  mer_id: number;
  merchant_name: string;
  product_no: string;
  title: string;
  subtitle: string;
  cover_image: string;
  cover_url: string;
  images: string[];
  brand: string;
  model: string;
  car_type: string;
  seats: number;
  seats_text: string;
  gearbox: number;
  gearbox_text: string;
  license_plate: string;
  daily_price: number;
  deposit_type: number;
  deposit_type_text: string;
  deposit_amount: number;
  service_type: number;
  service_type_text: string;
  pickup_location: string;
  rating: number;
  reviews_count: number;
  insurance_info: string;
  delivery_service: number;
  delivery_service_text: string;
  features: string;
  description: string;
  available_stock: number;
  min_order_days: number;
  max_order_days: number;
  cancel_policy: string;
  created_at: string;
}

/** 车辆详情响应 */
export interface CarDetailResponse {
  code: number;
  message: string;
  data: CarDetailItem;
}

// ==================== 租车订单相关类型 ====================

/** 预创建订单请求参数 */
export interface PreCreateOrderParams {
  car_id?: number;
  pickup_date?: string;
  pickup_time?: string;
  return_date?: string;
  return_time?: string;
  pickup_location?: string;
  pickup_latitude?: number;
  pickup_longitude?: number;
  return_location?: string;
  return_latitude?: number;
  return_longitude?: number;
}

/** 预创建订单响应 */
export interface PreCreateOrderResponse {
  code: number;
  message: string;
  data: {
    order_id: number;
    order_no: string;
    total_amount: string;
    rent_days: number;
  };
}

/** 出行人信息 */
export interface TravelerInfo {
  name: string;
  phone?: string;
  traveler_type?: number;
  id_type?: number;
  id_number?: string;
  id_card_front?: string;
  id_card_back?: string;
  license_number?: string;
  license_type?: string;
  license_expire_date?: string;
  license_front?: string;
  license_back?: string;
}

/** 确认订单请求参数 */
export interface ConfirmOrderParams {
  order_no: string;
  contact_name: string;
  contact_phone: string;
  contact_email?: string;
  travelers?: TravelerInfo[];
  special_requests?: string[];
}

/** 确认订单响应 */
export interface ConfirmOrderResponse {
  code: number;
  message: string;
  data: {
    order_id: number;
    order_no: string;
    total_amount: string;
  };
}

/** 联系人信息 */
export interface ContactInfo {
  name: string;
  phone: string;
  email?: string;
}

/** 订单步骤项 */
export interface OrderStepItem {
  title: string;
  done: boolean;
}

/** 订单详情：车辆信息 */
export interface CarOrderDetailCarInfo {
  id: number;
  title: string;
  cover: string;
  brand: string;
  model: string;
  seats: number;
  seats_text: string;
  gearbox: number;
  displacement: string;
  has_driver: number;
  has_driver_text: string;
}

/** 订单详情：保险信息 */
export interface CarOrderDetailInsuranceInfo {
  type: number;
  type_text: string;
  price: string;
}

/** 订单详情：取/还车信息 */
export interface CarOrderDetailLocationInfo {
  date: string;
  time: string;
  location: string | null;
  latitude: number | null;
  longitude: number | null;
}

/** 订单详情：费用信息 */
export interface CarOrderDetailFeeInfo {
  daily_price: string;
  car_amount: string;
  original_price: string;
  total_amount: string;
  deposit_type: number;
  deposit_type_text: string;
  deposit_amount: string;
}

/** 订单详情：交接照片 */
export interface CarOrderDetailPhotos {
  front: string | null;
  side: string | null;
  rear: string | null;
}

export interface CarOrderDetailDriverInfo {
  name: string;
  phone: string;
  license_plate: string;
  remark: string; // 备注信息
}

export interface RefundInfo  {
  apply_desc: string;
  apply_images: string[];
  apply_reason: string;
  create_time: string;
  has_refund: number;
  refund_id: number;
  refund_money: number;
  refund_no: string;
  refund_status: number;
  refund_status_text: string;
  refuse_desc: string;
  update_time: string;
}

/** 订单详情响应 */
export interface CarOrderDetailResponse {
  code: number;
  message: string;
  data: {
    id: number;
    order_no: string;
    order_type: string;
    created_at: string;
    contact: ContactInfo;
    travelers: TravelerInfo[];
    special_requests: string[];
    status: number;
    status_text: string;
    status_desc: string;
    order_steps: OrderStepItem[];
    service_steps: OrderStepItem[];
    car: CarOrderDetailCarInfo;
    insurance: CarOrderDetailInsuranceInfo;
    pickup: CarOrderDetailLocationInfo;
    return: CarOrderDetailLocationInfo;
    rent_days: number;
    driver: CarOrderDetailDriverInfo | null;
    fee: CarOrderDetailFeeInfo;
    pickup_photos: CarOrderDetailPhotos;
    return_photos: CarOrderDetailPhotos;
    refund_info: RefundInfo;
    comment: unknown | null;
    rental_notice: unknown[];
    cancel_policy: unknown[];
    pay_type: unknown | null;
    pay_time: unknown | null;
    remark: unknown | null;
  };
}

/** 取消订单响应 */
export interface CancelOrderResponse {
  code: number;
  message: string;
}

// ==================== 接口调用 ====================

/**
 * 接口1：租车产品列表
 * GET /home/car/list
 */
export async function fetchCarList(params: {
  brand?: string;
  car_type?: string;
  sort?: string;
  page?: number;
  limit?: number;
  longitude?: string;
  latitude?: string;
}): Promise<CarListResponse["data"]> {
  console.log("[API] 获取租车产品列表:", "/home/car/list", params);

  const res = await http.get<CarListResponse>("/home/car/list", { params });

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "获取租车列表失败");
  }

  return res.data.data;
}

/**
 * 接口2：可用品牌列表
 * GET /home/car/brands
 */
export async function fetchBrandList(): Promise<BrandListResponse["data"]> {
  console.log("[API] 获取可用品牌列表:", "/home/car/brands");

  const res = await http.get<BrandListResponse>("/home/car/brands");

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "获取品牌列表失败");
  }

  return res.data.data;
}

/**
 * 接口3：车型分类列表
 * GET /home/car/car-types
 */
export async function fetchCarTypeList(): Promise<CarTypeListResponse["data"]> {
  console.log("[API] 获取车型分类列表:", "/home/car/car-types");

  const res = await http.get<CarTypeListResponse>("/home/car/car-types");

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "获取车型分类失败");
  }

  return res.data.data;
}

/**
 * 接口4：车辆详情
 * GET /home/car/detail?id={id}
 */
export async function fetchCarDetail(
  id: number,
): Promise<CarDetailResponse["data"]> {
  console.log("[API] 获取车辆详情:", "/home/car/detail", { id });

  const res = await http.get<CarDetailResponse>("/home/car/detail", {
    params: { id },
  });

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "获取车辆详情失败");
  }

  return res.data.data;
}

// ==================== 租车订单接口 ====================

/**
 * 接口5：预创建订单
 * POST /home/car-order/pre-create
 * 保存车辆信息（取还车日期、地点、保险），返回订单基本信息
 */
export async function preCreateOrder(
  params: PreCreateOrderParams,
): Promise<PreCreateOrderResponse["data"]> {
  console.log("[API] 预创建订单:", "/home/car-order/pre-create", params);

  const res = await http.post<PreCreateOrderResponse>(
    "/home/car-order/pre-create",
    params,
  );

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "预创建订单失败");
  }

  return res.data.data;
}

/**
 * 接口6：确认订单
 * POST /home/car-order/confirm
 * 保存联系人和出行人信息到已创建的订单
 */
export async function confirmOrder(
  params: ConfirmOrderParams,
): Promise<ConfirmOrderResponse["data"]> {
  console.log("[API] 确认订单:", "/home/car-order/confirm", params);

  const res = await http.post<ConfirmOrderResponse>(
    "/home/car-order/confirm",
    params,
  );

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "确认订单失败");
  }

  return res.data.data;
}

/**
 * 接口7：订单详情
 * GET /home/car-order/detail?id={id}
 * 获取订单详细信息
 */
export async function fetchCarOrderDetail(
  id: number,
): Promise<CarOrderDetailResponse["data"]> {
  console.log("[API] 获取订单详情:", "/home/car-order/detail", { id });

  const res = await http.get<CarOrderDetailResponse>("/home/car-order/detail", {
    params: { id },
  });

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "获取订单详情失败");
  }

  return res.data.data;
}

/**
 * 接口8：取消订单
 * POST /home/car-order/cancel?id={id}&reason={reason}
 * 取消待支付状态的订单
 */
export async function cancelCarOrder(
  id: number,
  reason?: string,
): Promise<CancelOrderResponse> {
  console.log("[API] 取消订单:", "/home/car-order/cancel", { id, reason });

  const res = await http.post<CancelOrderResponse>(
    "/home/car-order/cancel",
    null,
    {
      params: { id, reason },
    },
  );

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "取消订单失败");
  }

  return res.data;
}
